'use client';

import { RefObject, useEffect } from 'react';

import { ampli } from '../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import {
    TrackingProperties,
    useTrackEventAmplitude,
} from '../../core/Tracking/Amplitude/Amplitude';

import { useIntersectOnce } from './useIntersectOnce';

/**
 * Hook that tracks when a block enters the viewport
 * Triggers an Amplitude `blockViewed` event once when the block becomes visible
 *
 * @param blockRef - Ref of the element to track
 * @param block - The block name to track
 * @param intersectOptions - Optional IntersectionObserver options (threshold defaults to 0.5)
 *
 * @example
 * ```tsx
 * function MySection() {
 *   const sectionRef = useRef<HTMLElement>(null);
 *   useTrackBlockViewed(sectionRef, 'cover');
 *   return <section ref={sectionRef}>Your content here</section>;
 * }
 * ```
 *
 * @example
 * ```tsx
 * function MySection() {
 *   const sectionRef = useRef<HTMLElement>(null);
 *   useTrackBlockViewed(sectionRef, 'agencies', { threshold: 0.8 });
 *   return <section ref={sectionRef}>Your content here</section>;
 * }
 * ```
 */
export function useTrackBlockViewed(
    blockRef: RefObject<HTMLElement | null>,
    block: TrackingProperties<typeof ampli.blockViewed>['block'],
    intersectOptions?: IntersectionObserverInit,
) {
    const { trackEvent } = useTrackEventAmplitude();

    const hasIntersected = useIntersectOnce(blockRef, {
        threshold: 0.5,
        ...intersectOptions,
    });

    useEffect(() => {
        if (hasIntersected) {
            trackEvent((ampli, defaultProperties) => {
                ampli.blockViewed({
                    ...defaultProperties,
                    block,
                });
            });
        }
    }, [hasIntersected, trackEvent, block]);
}
